import { uniq } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import ReactFlow from 'reactflow';
import styled from 'styled-components/macro';
import { Header1 } from '../../shared/Headers';
import { colourPrimaryDarkPurple } from '../../styling/colours';
import { HeatSourceResponse, PipeResponse } from '../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { getTreeWithStats } from '../shared/quote-or-design-form/map/map-helpers/getTreeWithStats';
import { generateMapFromTree } from '../shared/quote-or-design-form/map/map-helpers/mapHelpers';
import { getStatsData, Tree } from '../shared/quote-or-design-form/map/map-helpers/treeHelpers';
import { MapNodeType } from '../shared/quote-or-design-form/map/mapNode';
import { PumpTreeNode, TreeNodeType } from '../shared/quote-or-design-form/map/treeNode';
import { ReadOnlyHeatSource } from './map/ReadOnlyHeatSource';
import { ReadOnlyManifold } from './map/ReadOnlyManifold';
import { ReadOnlyPipe } from './map/ReadOnlyPipe';
import { ReadOnlyPump } from './map/ReadOnlyPump';
import { PdfStaticReferenceDataContextProvider } from './map/usePdfStaticReferenceData';
import { PdfDetailResponse, PdfPumpResponse, PdfDataUnit } from './PdfDataResponse';
import { ManifoldDetailsTable } from './ManifoldDetailsTable';
import { PumpTable } from './PumpTable';
import { SystemComponentSizingTable } from './SystemComponentSizingTable';
import { PdfPage } from './manifold-diagram/PdfPage';

type Props = {
  unit: PdfDataUnit;
  unitPumps: Array<PdfPumpResponse>;
  manifoldPumps: Array<PdfPumpResponse>;
  heatSources: Array<HeatSourceResponse>;
  pipes: Array<PipeResponse>;
  totalNumberOfPages: number;
  pdfDetail: PdfDetailResponse;
};

export const MapPage = ({
  unit,
  unitPumps,
  manifoldPumps,
  heatSources,
  pipes,
  totalNumberOfPages,
  pdfDetail,
}: Props) => {
  const nodeTypes = useMemo(
    () => ({
      [MapNodeType.HeatSource]: ReadOnlyHeatSource,
      [MapNodeType.Pipe]: ReadOnlyPipe,
      [MapNodeType.Pump]: ReadOnlyPump,
      [MapNodeType.Manifold]: ReadOnlyManifold,
    }),
    [],
  );

  useEffect(() => {
    // in order to get around a bug in Chrome, we have to set it to blank,
    // and then to what we want, so that it picks up the change
    document.title = '';
    document.title = `QR${pdfDetail.qrNumber} ${pdfDetail.designTitle} System ${pdfDetail.currentRevision}`;
  }, [pdfDetail.currentRevision, pdfDetail.designTitle, pdfDetail.qrNumber]);

  const tree = JSON.parse(unit.pipelineMapJson) as Tree;
  const statsData = getStatsData(unit.manifolds, unitPumps, manifoldPumps, heatSources, pipes);
  const treeWithStats = getTreeWithStats(tree, statsData);

  const pumpNodesInTree = Object.values(treeWithStats).filter(
    (node): node is PumpTreeNode => node.nodeType === TreeNodeType.Pump,
  );
  const unitPumpsFromMap = uniq(
    pumpNodesInTree
      .map(pump => unitPumps.find(p => p.displayName === pump.fieldData.pump))
      .filter((pump): pump is PdfPumpResponse => pump !== undefined),
  );

  const manifoldPumpIds = uniq(
    unit.manifolds.map(manifold => manifold.pumpId).filter(manifoldPumpId => manifoldPumpId !== null),
  );
  const manifoldPumpsFromMap = manifoldPumpIds
    .map(id => manifoldPumps.find(mp => mp.partId === id))
    .filter((pump): pump is PdfPumpResponse => pump !== undefined);

  const { map, edges } = generateMapFromTree(treeWithStats, 'Small', false);

  const sidebarDetails = {
    pageName: 'System and Manifolds',
    pageNumber: 1,
    type: (unit.heatSource === 'HeatPump' ? 'HeatPump' : 'Boiler') as 'HeatPump' | 'Boiler',
    totalNumberOfPages: totalNumberOfPages,
    pdfDetail: pdfDetail,
  };

  return (
    <PdfPage sidebarDetails={sidebarDetails}>
      <MapPageContainer>
        <Header1>Underfloor Heating System Primary Pipework Schematic</Header1>
        <MapPageBody>
          <TableContainer>
            <SystemComponentSizingTable hasHeatPump={unit.heatSource === 'HeatPump'} pdfDetail={pdfDetail} />
            <ManifoldDetailsTable manifolds={unit.manifolds} />
            <PumpTable pumps={[...unitPumpsFromMap, ...manifoldPumpsFromMap]} />
          </TableContainer>
          <Map>
            <PdfStaticReferenceDataContextProvider pipes={pipes} manifolds={unit.manifolds}>
              <ReactFlow
                nodes={map}
                edges={edges}
                nodeTypes={nodeTypes}
                elementsSelectable={false}
                fitView={true}
                minZoom={0.01}
                panOnDrag={false}
                zoomOnScroll={false}
                preventScrolling={false}
              />
            </PdfStaticReferenceDataContextProvider>
          </Map>
        </MapPageBody>
      </MapPageContainer>
    </PdfPage>
  );
};

const Map = styled.div`
  width: 1400px;
  height: 1400px;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  width: 400px;

  h2 {
    font-size: 24px;
    background-color: ${colourPrimaryDarkPurple};
    color: white;
    text-align: center;
  }
`;

const MapPageContainer = styled.div`
  display: flex;
  flex-direction: column;

  h1 {
    color: ${colourPrimaryDarkPurple};
    text-align: center;
  }
`;

const MapPageBody = styled.div`
  display: flex;
`;
